import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ArrowRight, Sun, Moon } from "lucide-react";
import { useTheme } from "next-themes";
import { useTranslation } from "@/i18n/useTranslation";
import VertLogo from "@/components/VertLogo";

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);
  const location = useLocation();
  const { theme, setTheme } = useTheme();
  const { t, locale, setLocale } = useTranslation();

  const navLinks = [
    { label: t("nav.services"), href: "/services" },
    { label: t("nav.solutions"), href: "/solutions" },
    { label: locale === "fr" ? "Industries" : "Industries", href: "/industries" },
    { label: t("nav.insights"), href: "/insights" },
    { label: t("nav.about"), href: "/about" },
    { label: t("nav.contact"), href: "/contact" },
  ];

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => { setOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  const toggleTheme = () => setTheme(theme === "dark" ? "light" : "dark");
  const toggleLocale = () => setLocale(locale === "en" ? "fr" : "en");

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${
      scrolled
        ? "glass border-b border-border/15 shadow-[0_1px_8px_hsl(var(--foreground)/0.04),0_0_1px_hsl(var(--foreground)/0.02)]"
        : "bg-transparent border-b border-transparent"
    }`}>
      <nav className="container-wide flex items-center justify-between h-14 lg:h-16">
        <Link to="/" className="flex items-center group">
          <VertLogo className="h-5 lg:h-[22px] w-auto text-foreground transition-opacity duration-300 group-hover:opacity-70" />
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {navLinks.map((link) => {
            const isActive = location.pathname.startsWith(link.href);
            return (
              <Link
                key={link.href}
                to={link.href}
                className={`relative font-mono text-[10.5px] font-medium tracking-[0.12em] uppercase transition-colors duration-400 hover:text-foreground ${
                  isActive ? "text-foreground" : "text-muted-foreground/70"
                }`}
              >
                {link.label}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-px bg-primary/40 animate-fade-in" />
                )}
              </Link>
            );
          })}

          {mounted && (
            <>
              <button
                onClick={toggleLocale}
                className="w-7 h-7 rounded-sm flex items-center justify-center font-mono text-[10px] font-medium tracking-[0.12em] text-muted-foreground/80 hover:text-foreground transition-colors duration-400"
                aria-label={locale === "en" ? "Switch to French" : "Switch to English"}
              >
                {locale === "en" ? "FR" : "EN"}
              </button>
              <button
                onClick={toggleTheme}
                className="w-7 h-7 rounded-sm flex items-center justify-center text-muted-foreground/70 hover:text-foreground transition-colors duration-400"
                aria-label="Toggle dark mode"
              >
                {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              </button>
            </>
          )}

          <a
            href="https://calendly.com/d/cvpp-rwv-v5j"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center justify-center gap-2 h-8 px-5 rounded-sm font-mono text-[10px] font-medium tracking-[0.08em] uppercase transition-all duration-600 ${
              scrolled
                ? "bg-foreground text-background border border-foreground hover:bg-transparent hover:text-foreground"
                : "bg-transparent text-foreground border border-foreground/8 hover:border-primary/35 hover:text-primary"
            }`}
          >
            {t("nav.talkToUs")}
            <ArrowRight className="w-3 h-3" />
          </a>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          {mounted && (
            <>
              <button
                onClick={toggleLocale}
                className="p-2 font-mono text-[10px] font-medium tracking-wider text-muted-foreground hover:text-foreground transition-colors"
                aria-label={locale === "en" ? "Switch to French" : "Switch to English"}
              >
                {locale === "en" ? "FR" : "EN"}
              </button>
              <button
                onClick={toggleTheme}
                className="p-2 text-muted-foreground/70 hover:text-foreground transition-colors"
                aria-label="Toggle dark mode"
              >
                {theme === "dark" ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
              </button>
            </>
          )}
          <button className="p-2 -mr-2 text-foreground" onClick={() => setOpen(!open)} aria-label="Toggle menu">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden glass border-t border-border/15 animate-fade-in">
          <div className="container-wide py-8 flex flex-col gap-0">
            {navLinks.map((link, i) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setOpen(false)}
                className={`font-mono text-[11px] tracking-[0.1em] uppercase py-4 border-b border-border/8 transition-all hover:text-foreground opacity-0 animate-fade-up ${
                  location.pathname.startsWith(link.href) ? "text-foreground" : "text-muted-foreground/70"
                }`}
                style={{ animationDelay: `${0.05 + i * 0.06}s` }}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://calendly.com/d/cvpp-rwv-v5j"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-8 btn-primary opacity-0 animate-fade-up"
              style={{ animationDelay: "0.35s" }}
            >
              {t("nav.talkToUs")}
              <ArrowRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
